/**
 * Prop-layer rendering — draws props as small coloured rectangles with a
 * label (procedural fallback per Q-C8 / CLIENT_DESIGN §4.6).
 *
 * The renderer places one rectangle per in-viewport prop on top of the
 * floor-tile diamond at the prop's position. Real sprite art replaces
 * this in Phase 4 polish.
 */

import { Container, Graphics, Text } from 'pixi.js';

import type { WorldModel } from '../world/model';
import {
  tileToIso,
  TILE_HEIGHT_PX,
  TILE_WIDTH_PX,
  visibleTileRange,
  type CameraState,
} from './iso';
import { propKindColor } from './palette';
import type { SceneLayers } from './scene';

const PROP_BOX_WIDTH = 40;
const PROP_BOX_HEIGHT = 48;

export interface PropRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ) => void;
  destroy: () => void;
}

export function createPropRenderer(layers: SceneLayers): PropRenderer {
  // All props share one `Container`; each prop is a small Graphics + Text
  // pair. We recycle Container children rather than destroy+recreate each
  // frame so the allocator doesn't churn.
  const root = new Container({ label: 'props-root' });
  layers.propLayer.addChild(root);

  // Simple pool keyed by PropId. When a prop leaves the viewport, its
  // children hide (visible = false) but stay allocated.
  const pool = new Map<number, { graphics: Graphics; label: Text }>();

  const render = (
    model: WorldModel,
    cam: CameraState,
    viewportW: number,
    viewportH: number,
  ): void => {
    if (model.props.size === 0) {
      // Hide everything currently in the pool; keep allocation for next frame.
      for (const entry of pool.values()) {
        entry.graphics.visible = false;
        entry.label.visible = false;
      }
      return;
    }

    const r = visibleTileRange(cam, viewportW, viewportH, 2);
    const seen = new Set<number>();

    for (const prop of model.props.values()) {
      const [px, py] = prop.position;
      if (px < r.minX || px > r.maxX || py < r.minY || py > r.maxY) continue;

      let entry = pool.get(prop.id);
      if (!entry) {
        const graphics = new Graphics();
        const label = new Text({
          text: '',
          style: {
            fill: 0xf0f0f0,
            fontSize: 9,
            fontFamily: 'sans-serif',
          },
        });
        label.anchor.set(0.5, 1);
        root.addChild(graphics, label);
        entry = { graphics, label };
        pool.set(prop.id, entry);
      }

      const iso = tileToIso(px, py);
      // Centre the box on the tile centre, offset upward so it sits on
      // the diamond's floor (not floating above it).
      const boxTopY = iso.y + TILE_HEIGHT_PX / 2 - PROP_BOX_HEIGHT;
      entry.graphics.clear();
      entry.graphics.rect(
        iso.x - PROP_BOX_WIDTH / 2,
        boxTopY,
        PROP_BOX_WIDTH,
        PROP_BOX_HEIGHT,
      );
      entry.graphics.fill({ color: propKindColor(prop.kind), alpha: 1 });
      entry.graphics.stroke({ color: 0x000000, width: 1, alpha: 0.4 });
      entry.graphics.visible = true;

      entry.label.text = `k${prop.kind}`;
      entry.label.position.set(iso.x, boxTopY);
      entry.label.visible = true;

      seen.add(prop.id);
    }

    // Hide entries whose props left the viewport this frame.
    for (const [id, entry] of pool) {
      if (seen.has(id)) continue;
      entry.graphics.visible = false;
      entry.label.visible = false;
    }

    // Tile-width used via the iso constants — keep the unused import
    // suppressor happy in case bundler tree-shakes loose.
    void TILE_WIDTH_PX;
  };

  return {
    render,
    destroy: () => {
      for (const entry of pool.values()) {
        entry.graphics.destroy();
        entry.label.destroy();
      }
      pool.clear();
      root.destroy();
    },
  };
}
